import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaCalculator,
  FaEuroSign,
  FaSave,
  FaSearch,
  FaTimes,
  FaUserCheck,
} from "react-icons/fa";
import { toast } from "react-toastify";

import ReglementDrawer from "./ReglementDrawer";

import { createReglementInscription } from "../../../../../services/reglementinscriptionServices";
import { getAllAdherentsList } from "../../../../../services/adherentServices";
import { getSettings } from "../../../../../services/settingsServices";

import {
  extractArray,
  fmtEUR,
  neighborsFromSeason,
  STATUTS_REGLEMENT,
  TYPES_REGLEMENT,
  toNumber,
} from "../reglement.helpers";

const initialForm = {
  adherent_id: "",
  saison: "",
  montant_lic_assurance: "",
  montant_cotisation: "",
  reduction_foyer: "",
  pass_sport: "",
  reduction_saison: "",
  reduction_passplus: "",
  reduction_labaz: "",
  type_reglement: "",
  nb_reglement: "1",
  observations: "",
  statut_reglement: "",
};

function FieldLabel({ children, required = false }) {
  return (
    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SectionCard({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-900 dark:text-white">{title}</h3>
          {subtitle ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>

      {children}
    </div>
  );
}

function AdherentPicker({ adherents = [], value, onSelect, loading = false }) {
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const selected = useMemo(() => {
    return adherents.find((adherent) => String(adherent.id) === String(value));
  }, [adherents, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    const sorted = adherents
      .slice()
      .sort((a, b) =>
        `${a?.nom || ""} ${a?.prenom || ""}`.localeCompare(
          `${b?.nom || ""} ${b?.prenom || ""}`,
          "fr",
          { sensitivity: "base" }
        )
      );

    if (!q) return sorted.slice(0, 60);

    return sorted
      .filter((adherent) => {
        const text = [
          adherent?.nom,
          adherent?.prenom,
          adherent?.email,
          adherent?.licence_number,
          adherent?.commune,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return text.includes(q);
      })
      .slice(0, 60);
  }, [adherents, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const choose = (adherent) => {
    onSelect?.(adherent || null);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (event) => {
    if (!open && ["ArrowDown", "Enter"].includes(event.key)) {
      setOpen(true);
      return;
    }

    if (!open) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((value) => Math.min(value + 1, filtered.length - 1));
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((value) => Math.max(value - 1, 0));
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const item = filtered[highlight];
      if (item) choose(item);
    }

    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <FieldLabel required>Adhérent</FieldLabel>

      {selected ? (
        <div className="mb-2 flex items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-500/20 dark:bg-emerald-500/10">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
              {selected.nom} {selected.prenom}
            </p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
              {selected.email || "Email non renseigné"} · ID {selected.id}
            </p>
          </div>

          <button
            type="button"
            onClick={() => choose(null)}
            className="shrink-0 rounded-xl bg-white px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Changer
          </button>
        </div>
      ) : null}

      <div className="relative">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

        <input
          ref={inputRef}
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Rechercher nom, prénom, email, licence..."
          disabled={loading}
          className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        />
      </div>

      {open ? (
        <div
          className="absolute z-[9999] mt-2 max-h-80 w-full overflow-auto rounded-[22px] border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-950"
          onMouseDown={(event) => event.preventDefault()}
        >
          {loading ? (
            <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
              Chargement des adhérents...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
              Aucun adhérent trouvé.
            </div>
          ) : (
            filtered.map((adherent, index) => (
              <button
                type="button"
                key={adherent.id}
                onClick={() => choose(adherent)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition ${
                  index === highlight
                    ? "bg-indigo-50 dark:bg-indigo-500/10"
                    : "hover:bg-slate-50 dark:hover:bg-slate-900"
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                    {adherent.nom} {adherent.prenom}
                  </p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {adherent.email || "Email non renseigné"} · ID {adherent.id}
                  </p>
                </div>

                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  Choisir
                </span>
              </button>
            ))
          )}

          {!loading && filtered.length >= 60 ? (
            <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
              Affichage limité à 60 résultats. Affinez la recherche.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function CreateReglementInscriptionDrawer({
  isOpen,
  onClose,
  refreshList,
  syncDossierValidateReglement,
}) {
  const [adherents, setAdherents] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");

  const [loadingContext, setLoadingContext] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(initialForm);

  const liveTotal = useMemo(() => {
    return (
      toNumber(form.montant_lic_assurance) +
      toNumber(form.montant_cotisation) +
      toNumber(form.reduction_foyer) +
      toNumber(form.pass_sport) +
      toNumber(form.reduction_saison) +
      toNumber(form.reduction_passplus) +
      toNumber(form.reduction_labaz)
    );
  }, [form]);

  const loadContext = useCallback(async () => {
    try {
      setLoadingContext(true);

      const [adherentsResponse, settingsResponse] = await Promise.all([
        getAllAdherentsList(),
        getSettings(),
      ]);

      const adherentsRows = extractArray(adherentsResponse);
      const settingsRows = extractArray(settingsResponse);
      const active = settingsRows?.[0]?.saison_active || "";

      setAdherents(adherentsRows);
      setActiveSeason(active);
      setSeasons(neighborsFromSeason(active));

      setForm((prev) => ({
        ...prev,
        saison: prev.saison || active || "",
      }));
    } catch (error) {
      console.error("Erreur chargement contexte règlement :", error);
      toast.error("Impossible de charger les adhérents ou les paramètres.");
    } finally {
      setLoadingContext(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setForm(initialForm);
    setSaving(false);
    loadContext();
  }, [isOpen, loadContext]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const canSubmit =
    !saving &&
    !loadingContext &&
    String(form.adherent_id || "").trim() !== "" &&
    String(form.statut_reglement || "").trim() !== "";

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    try {
      setSaving(true);

      const saison = form.saison || activeSeason || "";

      const payload = {
        adherent_id: form.adherent_id,
        saison,
        montant_lic_assurance: toNumber(form.montant_lic_assurance),
        montant_cotisation: toNumber(form.montant_cotisation),
        reduction_foyer: toNumber(form.reduction_foyer),
        pass_sport: toNumber(form.pass_sport),
        reduction_saison: toNumber(form.reduction_saison),
        reduction_passplus: toNumber(form.reduction_passplus),
        reduction_labaz: toNumber(form.reduction_labaz),
        type_reglement: form.type_reglement || "",
        nb_reglement: (() => {
          const value = Number(form.nb_reglement);
          return Number.isFinite(value) && value > 0 ? value : 1;
        })(),
        observations: form.observations || "",
        statut_reglement: form.statut_reglement,
      };

      await createReglementInscription(payload);

      await syncDossierValidateReglement?.(
        payload.adherent_id,
        payload.saison,
        payload.statut_reglement
      );

      toast.success("Règlement créé avec succès.");

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur création règlement :", error);
      toast.error("Erreur lors de la création du règlement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReglementDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Créer un règlement"
      subtitle="Ajoutez un règlement d'inscription pour un adhérent et synchronisez son dossier."
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <SectionCard
            icon={<FaUserCheck />}
            title="Adhérent & saison"
            subtitle="Seuls l'adhérent et le statut sont obligatoires."
          >
            <div className="grid gap-4 lg:grid-cols-2">
              <AdherentPicker
                adherents={adherents}
                value={form.adherent_id}
                loading={loadingContext}
                onSelect={(adherent) =>
                  setForm((prev) => ({
                    ...prev,
                    adherent_id: adherent?.id || "",
                  }))
                }
              />

              <div>
                <FieldLabel>Saison</FieldLabel>
                <select
                  name="saison"
                  value={form.saison}
                  onChange={handleChange}
                  disabled={loadingContext}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">
                    {activeSeason
                      ? `Saison active par défaut : ${activeSeason}`
                      : "Laisser vide"}
                  </option>

                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </select>

                <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Si le champ est vide, la saison active sera utilisée.
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaEuroSign />}
            title="Montants"
            subtitle="Renseignez les montants. Les réductions doivent être saisies en négatif."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel>Licence & assurance</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="montant_lic_assurance"
                  value={form.montant_lic_assurance}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Cotisation</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="montant_cotisation"
                  value={form.montant_cotisation}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Réduction foyer</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="reduction_foyer"
                  value={form.reduction_foyer}
                  onChange={handleChange}
                  placeholder="-20.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Pass'Sport</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="pass_sport"
                  value={form.pass_sport}
                  onChange={handleChange}
                  placeholder="-70.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Réduction saison</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="reduction_saison"
                  value={form.reduction_saison}
                  onChange={handleChange}
                  placeholder="-10.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Réduction Pass+</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="reduction_passplus"
                  value={form.reduction_passplus}
                  onChange={handleChange}
                  placeholder="-30.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Réduction Labaz</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  name="reduction_labaz"
                  value={form.reduction_labaz}
                  onChange={handleChange}
                  placeholder="-50.00"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="flex items-end">
                <div className="w-full rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-500/20 dark:bg-indigo-500/10">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-indigo-700 dark:text-indigo-300">
                    Total provisoire
                  </p>
                  <p className="mt-1 text-2xl font-extrabold text-slate-950 dark:text-white">
                    {fmtEUR(liveTotal)}
                  </p>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaCalculator />}
            title="Mode & statut"
            subtitle="Le statut mettra aussi à jour la validation du règlement dans le dossier d'inscription."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel>Type de règlement</FieldLabel>
                <select
                  name="type_reglement"
                  value={form.type_reglement}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Sélectionner —</option>
                  {TYPES_REGLEMENT.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <FieldLabel>Nombre de règlements</FieldLabel>
                <input
                  type="number"
                  min="1"
                  name="nb_reglement"
                  value={form.nb_reglement}
                  onChange={handleChange}
                  placeholder="1"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel required>Statut</FieldLabel>
                <select
                  name="statut_reglement"
                  value={form.statut_reglement}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Sélectionner —</option>
                  {STATUTS_REGLEMENT.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <FieldLabel>Observations</FieldLabel>
                <textarea
                  name="observations"
                  value={form.observations}
                  onChange={handleChange}
                  rows={4}
                  placeholder="N° Pass'Sport, Pass+, Labaz, détails du règlement, remarques..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Création..." : "Créer"}
          </button>
        </div>
      </form>
    </ReglementDrawer>
  );
}